import { objectSpread2 as _objectSpread2 } from './_virtual/_rollupPluginBabelHelpers.js';
import validateInput from './validators/mortgage-calculator.validator.js';
import { DFI_PERCENT, MANAGEMENT_VALUE, ANNUAL_INTEREST_RATE, MONTHLY_HOUSEHOLD_INCOMES_PERCENT, MPI_LIST, MONTHS_OF_YEARS } from './constants.js';

var fixNum = function fixNum(num) {
  return Number(num.toFixed(2));
};

var getLoanAmount = function getLoanAmount(salePrice, downPayment) {
  return salePrice - downPayment;
};

var getNumberOfInstallments = function getNumberOfInstallments(loanTerm) {
  return loanTerm * MONTHS_OF_YEARS;
};

var getAmortizationValue = function getAmortizationValue(loanAmount, numberOfInstallments) {
  return fixNum(loanAmount / numberOfInstallments);
};

var getMipPercentPerAge = function getMipPercentPerAge(age) {
  var rate = MPI_LIST.reduce(function (res, mpi) {
    if (mpi.valid(age)) {
      return mpi.rate / 100;
    }

    return res;
  }, 0);
  return rate;
};

var getMipValue = function getMipValue(_ref) {
  var loanAmount = _ref.loanAmount,
      amortizationValue = _ref.amortizationValue,
      installmentNumber = _ref.installmentNumber,
      mipPercentPerAge = _ref.mipPercentPerAge;
  return fixNum((loanAmount - amortizationValue * installmentNumber) * mipPercentPerAge);
};

var getMonthlyInterestRate = function getMonthlyInterestRate() {
  return Math.pow(1 + ANNUAL_INTEREST_RATE / 100, 1 / 12) - 1;
};

var getDfiValue = function getDfiValue(salePrice) {
  return fixNum(salePrice * (DFI_PERCENT / 100));
};

var getDiffRecurringDecimal = function getDiffRecurringDecimal(_ref2) {
  var loanAmount = _ref2.loanAmount,
      numberOfInstallments = _ref2.numberOfInstallments,
      amortizationValue = _ref2.amortizationValue;
  var totalAmortizationValue = amortizationValue * numberOfInstallments;
  return loanAmount === totalAmortizationValue ? 0 : loanAmount - totalAmortizationValue;
};

var getInstallmentValue = function getInstallmentValue(_ref3) {
  var amortizationValue = _ref3.amortizationValue,
      interestValue = _ref3.interestValue,
      mipValue = _ref3.mipValue,
      dfiValue = _ref3.dfiValue,
      isLastInstallment = _ref3.isLastInstallment,
      recurringDecimal = _ref3.recurringDecimal;
  var mip = !isLastInstallment ? mipValue : 0;
  var dfi = !isLastInstallment ? dfiValue : 0;
  var amortization = isLastInstallment && recurringDecimal ? recurringDecimal + amortizationValue : amortizationValue;
  return fixNum(amortization + interestValue + MANAGEMENT_VALUE + mip + dfi);
};

var getInterestValue = function getInterestValue(_ref4) {
  var loanAmount = _ref4.loanAmount,
      amortizationValue = _ref4.amortizationValue,
      installmentNumber = _ref4.installmentNumber,
      monthlyInterestRate = _ref4.monthlyInterestRate;
  return fixNum((loanAmount - amortizationValue * (installmentNumber - 1)) * monthlyInterestRate);
};

var getMonthlyHouseholdIncomes = function getMonthlyHouseholdIncomes(firsInstallmentAmount) {
  return fixNum(firsInstallmentAmount / MONTHLY_HOUSEHOLD_INCOMES_PERCENT);
};
/**
 * Calculate the mortgage amount based on sale price, down payment, loan term and age
 * @param {Object} data required information
 * @param {Number} data.salePrice property sale price
 * @param {Number} data.downPayment initial mortgage payment
 * @param {Number} data.loanTerm loan term in years
 * @param {Number} data.age age of the borrower
 * @returns {Object} containing loanAmount, firstInstallment, lastInstallment,
 * monthlyHouseholdIncomes and annualInterestRate
 */


var calculateMortgage = function calculateMortgage(data) {
  var _calculateMortgageVal = validateInput(data),
      salePrice = _calculateMortgageVal.salePrice,
      downPayment = _calculateMortgageVal.downPayment,
      loanTerm = _calculateMortgageVal.loanTerm,
      age = _calculateMortgageVal.age;

  var loanAmount = getLoanAmount(salePrice, downPayment);
  var numberOfInstallments = getNumberOfInstallments(loanTerm);
  var amortizationValue = getAmortizationValue(loanAmount, numberOfInstallments);
  var mipPercentPerAge = getMipPercentPerAge(age);
  var monthlyInterestRate = getMonthlyInterestRate();
  var dfiValue = getDfiValue(salePrice);
  var recurringDecimal = getDiffRecurringDecimal({
    loanAmount: loanAmount,
    numberOfInstallments: numberOfInstallments,
    amortizationValue: amortizationValue
  });
  var settingsCommon = {
    amortizationValue: amortizationValue,
    dfiValue: dfiValue,
    recurringDecimal: recurringDecimal
  };
  var firstInstallment = getInstallmentValue(_objectSpread2(_objectSpread2({}, settingsCommon), {}, {
    mipValue: getMipValue({
      loanAmount: loanAmount,
      amortizationValue: amortizationValue,
      mipPercentPerAge: mipPercentPerAge,
      installmentNumber: 1
    }),
    interestValue: getInterestValue({
      loanAmount: loanAmount,
      amortizationValue: amortizationValue,
      monthlyInterestRate: monthlyInterestRate,
      installmentNumber: 1
    }),
    isLastInstallment: false
  }));
  var lastInstallment = getInstallmentValue(_objectSpread2(_objectSpread2({}, settingsCommon), {}, {
    mipValue: getMipValue({
      loanAmount: loanAmount,
      amortizationValue: amortizationValue,
      mipPercentPerAge: mipPercentPerAge,
      installmentNumber: numberOfInstallments
    }),
    interestValue: getInterestValue({
      loanAmount: loanAmount,
      amortizationValue: amortizationValue,
      monthlyInterestRate: monthlyInterestRate,
      installmentNumber: numberOfInstallments
    }),
    isLastInstallment: true
  }));
  return {
    annualInterestRate: ANNUAL_INTEREST_RATE,
    firstInstallment: firstInstallment,
    lastInstallment: lastInstallment,
    loanAmount: loanAmount,
    monthlyHouseholdIncomes: getMonthlyHouseholdIncomes(firstInstallment)
  };
};

export default calculateMortgage;
